import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Snackbar,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { CloudUpload as UploadIcon } from "@mui/icons-material";
import auctionService from "../../services/auctionService";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

interface PopupContent {
  title: string;
  description: string;
  button_text: string;
  logo_url: string;
}

const PopupContentEditor: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [content, setContent] = useState<PopupContent>({
    title: "",
    description: "",
    button_text: "",
    logo_url: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [language, setLanguage] = useState<string>("en");

  const fetchPopupContent = useCallback(async () => {
    try {
      setLoading(true);
      const data = await auctionService.getPopupContent(language);
      setContent(data);
      setPreviewUrl(data.logo_url);
    } catch (err: any) {
      setError(err.message || "Failed to load popup content");
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/");
      return;
    }
    fetchPopupContent();
  }, [user, navigate, fetchPopupContent]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUploadLogo = async () => {
    if (!selectedFile) return;

    try {
      setSaving(true);
      const result = await auctionService.uploadPopupLogo(selectedFile);
      setContent((prev) => ({ ...prev, logo_url: result.logo_url }));
      setSelectedFile(null);
      setSuccess("Logo uploaded successfully!");
    } catch (err: any) {
      setError(err.message || "Failed to upload logo");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await auctionService.updatePopupContent(content, language);
      setSuccess(
        `Popup content updated successfully for ${
          language === "en" ? "English" : "Spanish"
        }!`
      );
    } catch (err: any) {
      setError(err.message || "Failed to update popup content");
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange =
    (field: keyof PopupContent) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setContent((prev) => ({ ...prev, [field]: event.target.value }));
    };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Edit Popup Content
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        This content appears to non-authenticated users on the home page.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Language
        </Typography>
        <ToggleButtonGroup
          value={language}
          exclusive
          onChange={(event, newLanguage) => {
            if (newLanguage !== null) {
              setLanguage(newLanguage);
            }
          }}
          aria-label="language selection"
        >
          <ToggleButton value="en" aria-label="english">
            English
          </ToggleButton>
          <ToggleButton value="es" aria-label="spanish">
            Español
          </ToggleButton>
          <ToggleButton value="it" aria-label="italian">
            Italiano
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Content Settings
            </Typography>

            <TextField
              fullWidth
              label="Title"
              value={content.title}
              onChange={handleInputChange("title")}
              sx={{ mb: 2 }}
              helperText="The main title displayed in the popup"
            />

            <TextField
              fullWidth
              label="Description"
              value={content.description}
              onChange={handleInputChange("description")}
              multiline
              rows={4}
              sx={{ mb: 2 }}
              helperText="The description text displayed in the popup"
            />

            <TextField
              fullWidth
              label="Button Text"
              value={content.button_text}
              onChange={handleInputChange("button_text")}
              sx={{ mb: 3 }}
              helperText="The text displayed on the action button"
            />

            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
              sx={{ mr: 2 }}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>

            <Button
              variant="outlined"
              onClick={fetchPopupContent}
              disabled={saving}
            >
              Reset
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Logo Settings
            </Typography>

            <Card sx={{ mb: 2 }}>
              <CardMedia
                component="img"
                height="200"
                image={previewUrl || "/default-auction.png"}
                alt="Popup Logo"
                sx={{ objectFit: "contain" }}
              />
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Current Logo
                </Typography>
              </CardContent>
            </Card>

            <input
              accept="image/*"
              style={{ display: "none" }}
              id="logo-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="logo-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                fullWidth
                sx={{ mb: 2 }}
              >
                Select New Logo
              </Button>
            </label>

            {selectedFile && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleUploadLogo}
                disabled={saving}
                fullWidth
              >
                {saving ? "Uploading..." : "Upload Logo"}
              </Button>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Success Message */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert
          onClose={() => setSuccess(null)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PopupContentEditor;
